/**
 * Utility to calculate pixel width of text in Google SERP standard fonts
 * - Title font: 20px Arial (Google desktop snippet title)
 * - Description font: 14px Arial (Google desktop snippet snippet/description)
 */

// Proportional character widths for Arial in ratio relative to font size (1em)
const ARIAL_CHAR_WIDTHS = {
  // Uppercase
  A: 0.667, B: 0.667, C: 0.722, D: 0.722, E: 0.667, F: 0.611, G: 0.778, H: 0.722,
  I: 0.278, J: 0.500, K: 0.667, L: 0.556, M: 0.833, N: 0.722, O: 0.778, P: 0.667,
  Q: 0.778, R: 0.722, S: 0.667, T: 0.611, U: 0.722, V: 0.667, W: 0.944, X: 0.667,
  Y: 0.667, Z: 0.611, Æ: 0.944, Ø: 0.778, Å: 0.667,
  // Lowercase
  a: 0.556, b: 0.556, c: 0.500, d: 0.556, e: 0.556, f: 0.278, g: 0.556, h: 0.556,
  i: 0.222, j: 0.222, k: 0.500, l: 0.222, m: 0.833, n: 0.556, o: 0.556, p: 0.556,
  q: 0.556, r: 0.333, s: 0.500, t: 0.278, u: 0.556, v: 0.500, w: 0.722, x: 0.500,
  y: 0.500, z: 0.500, æ: 0.833, ø: 0.556, å: 0.556,
  // Digits
  '0': 0.556, '1': 0.556, '2': 0.556, '3': 0.556, '4': 0.556,
  '5': 0.556, '6': 0.556, '7': 0.556, '8': 0.556, '9': 0.556,
  // Symbols
  ' ': 0.278, '-': 0.333, '–': 0.556, '—': 1.000, '|': 0.278, '&': 0.667,
  ',': 0.278, '.': 0.278, ':': 0.278, ';': 0.278, '/': 0.278, '!': 0.278,
  '?': 0.556, '(': 0.333, ')': 0.333, '[': 0.333, ']': 0.333, '"': 0.355,
  "'": 0.191, '+': 0.584, '=': 0.584, '<': 0.584, '>': 0.584, '@': 1.015,
};

let canvasContext = null;

function getCanvasContext() {
  if (typeof document === 'undefined') return null;
  if (!canvasContext) {
    try {
      const canvas = document.createElement('canvas');
      canvasContext = canvas.getContext('2d');
    } catch {
      canvasContext = null;
    }
  }
  return canvasContext;
}

/**
 * Measures text pixel width accurately using canvas, or falling back to Arial matrix
 */
export function measureTextPx(text, fontSize = 20, fontName = 'Arial') {
  if (!text) return 0;
  const ctx = getCanvasContext();
  if (ctx) {
    ctx.font = `${fontSize}px ${fontName}, sans-serif`;
    return Math.round(ctx.measureText(text).width);
  }

  // Fallback using Arial character width matrix
  let totalWidth = 0;
  for (const char of text) {
    const ratio = ARIAL_CHAR_WIDTHS[char] ?? 0.556;
    totalWidth += ratio * fontSize;
  }
  return Math.round(totalWidth);
}

export function measureTitlePx(title) {
  return measureTextPx(title, 20, 'Arial');
}

export function measureDescriptionPx(description) {
  return measureTextPx(description, 14, 'Arial');
}

/**
 * Ensures title does not exceed maxPx (default 512 px in 20px Arial).
 * Trims gracefully at word boundaries if necessary.
 */
export function fitTitlePx(title, maxPx = 512) {
  if (!title) return '';
  const trimmed = title.trim();
  if (measureTitlePx(trimmed) <= maxPx) {
    return trimmed;
  }

  const words = trimmed.split(/\s+/);
  let result = '';
  for (const word of words) {
    const candidate = result ? `${result} ${word}` : word;
    if (measureTitlePx(candidate) <= maxPx) {
      result = candidate;
    } else {
      break;
    }
  }

  return result || trimmed.slice(0, 45).trim();
}

/**
 * Ensures description does not exceed maxPx (default 970 px in 14px Arial).
 * Trims gracefully at word or sentence boundaries if necessary.
 */
export function fitDescriptionPx(description, maxPx = 970) {
  if (!description) return '';
  const trimmed = description.trim();
  if (measureDescriptionPx(trimmed) <= maxPx) {
    return trimmed;
  }

  const words = trimmed.split(/\s+/);
  let result = '';
  for (const word of words) {
    const candidate = result ? `${result} ${word}` : word;
    if (measureDescriptionPx(candidate) <= maxPx) {
      result = candidate;
    } else {
      break;
    }
  }

  return result || trimmed.slice(0, 140).trim();
}
